import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ApiError } from '@/api/client';
import { useMissatgeError } from '@/api/errors';
import { esConflicteVersio, useAmbEtag } from '@/api/etag';
import {
  CRON,
  PERIODICITATS,
  SECCIONS,
  TIPUS_INFORME,
  TIPUS_PROGRAMAT,
  descarregaInformeActivacio,
  periodicitatDeCron,
  useDesaInformeProgramat,
  type FormatInforme,
  type InformeProgramatDto,
  type Periodicitat,
  type SeccioInforme,
  type TipusInforme,
} from '@/api/informes';
import { Icona, type NomIcona } from '@/components/Icona';
import { useIdioma } from '@/i18n/useFormat';
import { Toolbar } from '@/layout/Toolbar';
import { Buit, Camp } from '@/pages/operacio/comu';
import { useMe } from '@/permisos/MeContext';
import { SelectorAmbit } from './comu';

const FORMATS: FormatInforme[] = ['PDF', 'CSV', 'XLSX'];
const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const ICONA_TIPUS: Record<TipusInforme, { icona: NomIcona; color: string }> = {
  DETALL: { icona: 'doc-text', color: 'bg-red' },
  GLOBAL: { icona: 'chart-bar', color: 'bg-blue' },
  EQUIP: { icona: 'person-3', color: 'bg-purple' },
  AUDITORIA: { icona: 'clipboard', color: 'bg-gray' },
};

const ICONA_FORMAT: Record<FormatInforme, NomIcona> = {
  PDF: 'doc-text',
  CSV: 'list-bullet',
  XLSX: 'square-grid',
};

function tipusDeProgramat(p: InformeProgramatDto): TipusInforme {
  if (p.tipus === 'ACTIVACIONS') return 'DETALL';
  if (p.tipus === 'COMPARATIVA') return 'EQUIP';
  return 'GLOBAL';
}

export function W42Generador() {
  const { t } = useTranslation('informes');
  const missatgeError = useMissatgeError();
  const [params] = useSearchParams();
  const idEdicio = Number(params.get('id'));
  const editant = Number.isFinite(idEdicio) && idEdicio > 0;
  // Es carrega l'informe amb el seu ETag: és la versió que s'enviarà a If-Match en desar.
  const original = useAmbEtag<InformeProgramatDto>(
    editant ? `/informes/programats/${idEdicio}` : null,
  );
  if (editant && !original.data) {
    return (
      <section className="pantalla" data-screen="W-42">
        <Toolbar titol={t('w42.titolEdicio')} enrere="/analisi/informes" />
        <div className="content">
          {original.isError ? (
            <div className="avis-banda" role="alert">
              <Icona nom="exclamationmark-triangle" />
              {missatgeError(original.error)}
            </div>
          ) : (
            <Buit icona="doc-text">{t('comu.carregant')}</Buit>
          )}
        </div>
      </section>
    );
  }
  return (
    <FormulariInforme
      key={original.data?.etag ?? 'nou'}
      inicial={original.data?.data}
      etag={original.data?.etag ?? null}
      programa={params.get('programa') === '1'}
      onRecarrega={() => void original.refetch()}
    />
  );
}

function FormulariInforme({
  inicial,
  etag,
  programa: programaInicial,
  onRecarrega,
}: {
  inicial?: InformeProgramatDto;
  etag: string | null;
  programa: boolean;
  onRecarrega: () => void;
}) {
  const { t } = useTranslation('informes');
  const navigate = useNavigate();
  const idioma = useIdioma();
  const missatgeError = useMissatgeError();
  const { me } = useMe();
  const editant = inicial != null;
  const filtresInicials = inicial?.filtres ?? {};
  const [tipus, setTipus] = useState<TipusInforme>(() =>
    inicial ? tipusDeProgramat(inicial) : programaInicial ? 'GLOBAL' : 'DETALL',
  );
  const [nom, setNom] = useState(inicial?.nom ?? '');
  const [activacioId, setActivacioId] = useState(filtresInicials.activacioId ?? '');
  const [desde, setDesde] = useState(filtresInicials.desde ?? '');
  const [fins, setFins] = useState(filtresInicials.fins ?? '');
  const [ambit, setAmbit] = useState<{ departamentId: number | ''; areaId: number | '' }>({
    departamentId: filtresInicials.departamentId ? Number(filtresInicials.departamentId) : '',
    areaId: filtresInicials.areaId ? Number(filtresInicials.areaId) : '',
  });
  const [seccions, setSeccions] = useState<SeccioInforme[]>(() =>
    inicial
      ? (inicial.seccions ?? []).filter((x): x is SeccioInforme =>
          (SECCIONS as string[]).includes(x),
        )
      : [...SECCIONS],
  );
  const [format, setFormat] = useState<FormatInforme>(inicial?.format ?? 'PDF');
  const [programar, setProgramar] = useState(programaInicial || editant);
  const [periodicitat, setPeriodicitat] = useState<Periodicitat>(
    (inicial && periodicitatDeCron(inicial.cron)) || 'SETMANAL',
  );
  const [destinataris, setDestinataris] = useState(
    inicial ? (inicial.destinataris ?? []).join(', ') : (me?.email ?? ''),
  );
  const [error, setError] = useState<string | null>(null);
  const [conflicte, setConflicte] = useState(false);
  const [desat, setDesat] = useState(false);
  const desa = useDesaInformeProgramat();
  const carregat = inicial;

  const perActivacio = tipus === 'DETALL' || tipus === 'AUDITORIA';
  const programable = TIPUS_PROGRAMAT[tipus] != null;
  const id = Number(activacioId);
  const llistaDest = destinataris
    .split(/[,;\s]+/)
    .map((d) => d.trim())
    .filter(Boolean);

  const commuta = (s: SeccioInforme) =>
    setSeccions((v) => (v.includes(s) ? v.filter((x) => x !== s) : [...v, s]));

  const genera = async () => {
    setError(null);
    if (perActivacio && !(id > 0)) {
      setError(t('w42.errors.activacio'));
      return;
    }
    if (tipus === 'AUDITORIA') {
      navigate(`/analisi/tracabilitat/${id}`);
      return;
    }
    if (tipus === 'DETALL') {
      if (format === 'PDF') {
        navigate(`/analisi/informes/activacio/${id}`);
        return;
      }
      try {
        await descarregaInformeActivacio(id, `ACT-${id}`, format, idioma);
      } catch {
        setError(t('w42.errors.descarrega'));
      }
      return;
    }
    navigate(tipus === 'EQUIP' ? '/analisi/informes' : '/analisi/indicadors');
  };

  const programa = async () => {
    setError(null);
    setConflicte(false);
    setDesat(false);
    const tipusProgramat = TIPUS_PROGRAMAT[tipus];
    if (!tipusProgramat) return;
    if (!nom.trim()) {
      setError(t('w42.errors.nom'));
      return;
    }
    if (!llistaDest.length || llistaDest.some((d) => !EMAIL.test(d))) {
      setError(t('w42.errors.destinataris'));
      return;
    }
    const filtres: Record<string, string> = {};
    if (perActivacio && id > 0) filtres.activacioId = String(id);
    if (desde) filtres.desde = desde;
    if (fins) filtres.fins = fins;
    if (ambit.departamentId !== '') filtres.departamentId = String(ambit.departamentId);
    if (ambit.areaId !== '') filtres.areaId = String(ambit.areaId);
    const conservaTipus =
      carregat && tipusDeProgramat(carregat) === tipus ? carregat.tipus : tipusProgramat;
    const conservaCron =
      carregat && !periodicitatDeCron(carregat.cron) && periodicitat === 'SETMANAL'
        ? carregat.cron
        : CRON[periodicitat];
    try {
      await desa.mutateAsync({
        informe: {
          ...(carregat ?? {}),
          nom: nom.trim(),
          tipus: carregat ? conservaTipus : tipusProgramat,
          filtres,
          seccions,
          format,
          cron: carregat ? conservaCron : CRON[periodicitat],
          destinataris: llistaDest,
          actiu: carregat?.actiu ?? true,
        },
        etag,
      });
      if (editant) navigate('/analisi/informes');
      else setDesat(true);
    } catch (e) {
      if (esConflicteVersio(e)) {
        setConflicte(true);
        setError(missatgeError(e));
      } else {
        setError(
          e instanceof ApiError && e.camp
            ? t('w42.errors.camp', { camp: e.camp })
            : t('w42.errors.desa'),
        );
      }
    }
  };

  const desaVisible = programar && programable;

  return (
    <section className="pantalla" data-screen="W-42">
      <Toolbar
        titol={editant ? t('w42.titolEdicio') : t('titols.generador')}
        enrere="/analisi/informes"
      >
        {desaVisible ? (
          <button
            type="button"
            className="btn btn--primary btn--s"
            onClick={() => void programa()}
            disabled={desa.isPending}
          >
            <Icona nom="checkmark" />
            {t('w42.desaProgramacio')}
          </button>
        ) : (
          <button type="button" className="btn btn--primary btn--s" onClick={() => void genera()}>
            <Icona nom="doc-export" />
            {t('w42.genera')}
          </button>
        )}
      </Toolbar>
      <div className="content stack">
        {error ? (
          <div className="avis-banda" role="alert">
            <Icona nom="exclamationmark-triangle" />
            <span className="spacer">{error}</span>
            {conflicte ? (
              <button
                type="button"
                className="btn btn--xs"
                onClick={() => {
                  setConflicte(false);
                  setError(null);
                  onRecarrega();
                }}
              >
                <Icona nom="arrow-clockwise" />
                {t('w42.recarrega')}
              </button>
            ) : null}
          </div>
        ) : null}
        {desat ? (
          <div className="info-banda">
            <Icona nom="checkmark-circle" />
            <span>{t('w42.desat')}</span>
          </div>
        ) : null}
        <div className="grid grid-2" style={{ alignItems: 'start' }}>
          <div className="stack">
            <div className="panel">
              <div className="panel__head">
                <h3>{t('w42.tipus')}</h3>
              </div>
              <div className="panel__body opcions-informe">
                {TIPUS_INFORME.map((ti) => (
                  <button
                    key={ti}
                    type="button"
                    className="list-row"
                    aria-pressed={tipus === ti}
                    onClick={() => {
                      setTipus(ti);
                      if (!TIPUS_PROGRAMAT[ti]) setProgramar(false);
                    }}
                  >
                    <div className={`list-row__icon ${ICONA_TIPUS[ti].color}`}>
                      <Icona nom={ICONA_TIPUS[ti].icona} />
                    </div>
                    <div className="list-row__text">
                      <div className="list-row__label">{t(`tipusInforme.${ti}.nom`)}</div>
                      <div className="list-row__sub">{t(`tipusInforme.${ti}.desc`)}</div>
                    </div>
                    {tipus === ti ? (
                      <Icona nom="checkmark-circle" className="list-row__chev c-blue" />
                    ) : null}
                  </button>
                ))}
              </div>
            </div>
            <div className="panel">
              <div className="panel__head">
                <h3>{t('w42.filtres')}</h3>
              </div>
              <div className="panel__body form">
                {perActivacio ? (
                  <Camp etiqueta={t('w42.activacio')}>
                    <input
                      className="input"
                      inputMode="numeric"
                      value={activacioId}
                      placeholder={t('w42.activacioPlaceholder')}
                      onChange={(e) => setActivacioId(e.target.value.replace(/\D/g, ''))}
                    />
                  </Camp>
                ) : (
                  <>
                    <div className="grid grid-2" style={{ gap: 10 }}>
                      <Camp etiqueta={t('w42.desde')}>
                        <input
                          className="input"
                          type="date"
                          value={desde}
                          onChange={(e) => setDesde(e.target.value)}
                        />
                      </Camp>
                      <Camp etiqueta={t('w42.fins')}>
                        <input
                          className="input"
                          type="date"
                          value={fins}
                          onChange={(e) => setFins(e.target.value)}
                        />
                      </Camp>
                    </div>
                    <div className="camp">
                      <span className="camp__label">{t('w42.ambit')}</span>
                      <div className="row">
                        <SelectorAmbit {...ambit} onCanvia={setAmbit} />
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="stack">
            <div className="panel">
              <div className="panel__head">
                <h3>{t('w42.seccions')}</h3>
              </div>
              <div className="panel__body stack" style={{ gap: 12 }}>
                {SECCIONS.map((s) => (
                  <label key={s} className="switch-row">
                    <span>{t(`seccions.${s}`)}</span>
                    <input
                      type="checkbox"
                      className="switch"
                      checked={seccions.includes(s)}
                      onChange={() => commuta(s)}
                    />
                  </label>
                ))}
              </div>
            </div>
            <div className="panel">
              <div className="panel__head">
                <h3>{t('w42.formatProgramacio')}</h3>
              </div>
              <div className="panel__body form">
                <div className="chips" role="group" aria-label={t('w42.format')}>
                  {FORMATS.map((fo) => (
                    <button
                      key={fo}
                      type="button"
                      className="chip"
                      aria-pressed={format === fo}
                      onClick={() => setFormat(fo)}
                    >
                      <Icona nom={ICONA_FORMAT[fo]} />
                      {fo}
                    </button>
                  ))}
                </div>
                <label className="switch-row">
                  <Icona nom="calendar-clock" className="c-blue" />
                  <span>{t('w42.programar')}</span>
                  <input
                    type="checkbox"
                    className="switch"
                    checked={programar}
                    disabled={!programable || editant}
                    onChange={(e) => setProgramar(e.target.checked)}
                  />
                </label>
                {!programable ? (
                  <div className="t-caption dim">{t('w42.noProgramable')}</div>
                ) : null}
                {desaVisible ? (
                  <>
                    <div className="segmented" role="group" aria-label={t('w42.periodicitat')}>
                      {PERIODICITATS.map((p) => (
                        <button
                          key={p}
                          type="button"
                          aria-pressed={periodicitat === p}
                          onClick={() => setPeriodicitat(p)}
                        >
                          {t(`periodicitats.${p}`)}
                        </button>
                      ))}
                    </div>
                    <Camp etiqueta={t('w42.nom')}>
                      <input
                        className="input"
                        value={nom}
                        onChange={(e) => setNom(e.target.value)}
                      />
                    </Camp>
                    <Camp etiqueta={t('w42.destinataris')}>
                      <input
                        className="input"
                        value={destinataris}
                        placeholder={t('w42.destinatarisPlaceholder')}
                        onChange={(e) => setDestinataris(e.target.value)}
                      />
                    </Camp>
                  </>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
